// Notification Service - Handles notification API calls

import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';

export interface Notification {
  id: string;
  userId: string;
  tenantId: string;
  type: 'PROFILE_SUBMITTED' | 'PROFILE_APPROVED' | 'PROFILE_REJECTED' | 'PROFILE_RESUBMITTED' | 'DOCUMENT_VERIFIED' | 'DOCUMENT_REJECTED' | 'CHANGE_REQUEST_SUBMITTED' | 'CHANGE_REQUEST_APPROVED' | 'CHANGE_REQUEST_REJECTED' | 'EDEP_REPORT_READY';
  title: string;
  message: string;
  relatedId?: string;
  relatedType?: string;
  isRead: boolean;
  isSeen: boolean;
  createdAt: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  limit: number;
  offset: number;
}

export interface NotificationPreferences {
  id: string | null;
  userId: string;
  tenantId: string;
  emailProfileApproved: boolean;
  emailProfileRejected: boolean;
  emailProfileSubmitted: boolean;
  emailChangeRequest: boolean;
  inAppProfileApproved: boolean;
  inAppProfileRejected: boolean;
  inAppProfileSubmitted: boolean;
  inAppChangeRequest: boolean;
}

// Get all notifications for current user
export const getNotifications = async (limit?: number, offset?: number): Promise<NotificationsResponse> => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  if (offset) params.append('offset', offset.toString());

  const queryString = params.toString();
  const endpoint = `/notifications${queryString ? `?${queryString}` : ''}`;

  return apiGet<NotificationsResponse>(endpoint);
};

// Get unread notifications
export const getUnreadNotifications = async (): Promise<Notification[]> => {
  return apiGet<Notification[]>('/notifications/unread');
};

// Get count of unread notifications
export const getUnreadCount = async (): Promise<number> => {
  try {
    const data = await apiGet<{ count: number }>('/notifications/count');
    return data.count;
  } catch {
    // Return 0 silently on error to not break the UI
    return 0;
  }
};

// Get count of unseen notifications (for the badge)
export const getUnseenCount = async (): Promise<number> => {
  try {
    const data = await apiGet<{ count: number }>('/notifications/unseen-count');
    return data.count;
  } catch {
    // Return 0 silently on error to not break the UI
    return 0;
  }
};

// Mark all notifications as seen (when bell is clicked)
export const markAllAsSeen = async (): Promise<void> => {
  await apiPost<void>('/notifications/mark-seen');
};

// Mark a notification as read
export const markAsRead = async (notificationId: string): Promise<Notification> => {
  return apiPost<Notification>(`/notifications/${notificationId}/read`);
};

// Mark all notifications as read
export const markAllAsRead = async () => {
  return apiPost('/notifications/read-all');
};

// Delete a notification
export const deleteNotification = async (notificationId: string) => {
  return apiDelete(`/notifications/${notificationId}`);
};

// Delete all notifications
export const deleteAllNotifications = async () => {
  return apiDelete('/notifications');
};

// Get notification preferences
export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  return apiGet<NotificationPreferences>('/notifications/preferences');
};

// Update notification preferences
export const updateNotificationPreferences = async (
  preferences: Partial<Omit<NotificationPreferences, 'id' | 'userId' | 'tenantId'>>
): Promise<NotificationPreferences> => {
  return apiPut<NotificationPreferences>('/notifications/preferences', preferences);
};
